import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import RotatingQrImg from '../components/RotatingQrImg';
import './PublicTicketPage.css';

const isActiveTicketStatus = (status) => String(status || '').toUpperCase() === 'ISSUED';

const normalizeAmpInfo = (amp) => {
  const source = amp || {};
  const section = String(source.section || source.section_name || '').trim();
  const row = String(source.row || '').trim();
  const seat = String(source.seat_number ?? source.seat ?? '').trim();
  const eventDate = String(source.event_date || '').trim();
  return { section, row, seat, eventDate };
};

function PublicTicketPage() {
  const { ticketCode } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [walletAdded, setWalletAdded] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [geoState, setGeoState] = useState({
    status: 'locating',
    label: '',
  });
  const scanLineRef = useRef(null);
  const qrWrapRef = useRef(null);
  const animationFrameRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const res = await api.getPublicTicket(ticketCode);
      if (res?.success && res?.data) {
        setTicket(res.data);
        setWalletAdded(Boolean(res.data.wallet_added));
      } else {
        setError(res?.error?.message || 'Ticket not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketCode]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    const clockId = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(clockId);
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoState({ status: 'unsupported', label: 'Location unavailable' });
      return;
    }

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        const lat = Number(position.coords?.latitude || 0);
        const lng = Number(position.coords?.longitude || 0);
        setGeoState({
          status: 'ready',
          label: `${lat.toFixed(2)}, ${lng.toFixed(2)}`,
        });
      },
      () => {
        if (cancelled) return;
        setGeoState({ status: 'denied', label: 'Location permission denied' });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000,
      }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  // Animated red scan line — mirrors React Native ScanLine component
  useEffect(() => {
    if (!ticket?.qr_code || !qrWrapRef.current) return;

    const qrWrap = qrWrapRef.current;
    const scanLine = scanLineRef.current;
    if (!scanLine) return;

    let start = null;
    const duration = 2000; // 2s per cycle
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = (timestamp - start) % duration;
      const progress = elapsed / duration;
      // Ease in-out
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const qrHeight = qrWrap.offsetHeight;
      const y = eased * (qrHeight - 4);
      scanLine.style.transform = `translateY(${y}px)`;
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [ticket]);

  const handleAppleWallet = async () => {
    if (!ticket) return;
    const url = api.getPublicAppleWalletUrl(ticket.ticket_id, ticket.ticket_code);
    if (api.isIOSInAppBrowser && api.isIOSInAppBrowser()) {
      setWalletError('Apple Wallet requires Safari. Open this page in Safari, then try again.');
      return;
    }

    setWalletError('');
    setWalletAdded(true);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
    if (isIOS) {
      window.location.assign(url);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleGoogleWallet = async () => {
    if (!ticket) return;
    setWalletLoading(true);
    setWalletError('');
    try {
      const res = await api.getPublicGoogleWalletPass(ticket.ticket_id, ticket.ticket_code);
      if (res?.success && res?.save_url) {
        setWalletAdded(true);
        window.open(res.save_url, '_blank', 'noopener,noreferrer');
      } else {
        setWalletError(res?.error?.message || 'Failed to create Google Wallet pass');
      }
    } catch (err) {
      setWalletError(err.message || 'Failed to create Google Wallet pass');
    } finally {
      setWalletLoading(false);
    }
  };

  const timeLabel = new Date(nowTs).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const zoneLabel = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local time';
  const countdown = 30 - (Math.floor(nowTs / 1000) % 30);
  const showLiveTimer = !!ticket && isActiveTicketStatus(ticket.status) && !walletAdded;
  const ampInfo = normalizeAmpInfo(ticket?.amphitheater);
  const hasAmpInfo = ampInfo.section || ampInfo.row || ampInfo.seat || ampInfo.eventDate;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="ptp-container">
        <div className="ptp-loading">
          <div className="ptp-spinner" />
          <p>Loading your ticket…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ptp-container">
        <div className="ptp-error">
          <div className="ptp-error-icon">⚠</div>
          <h2>Ticket Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ptp-container">
      <div className="ptp-card">
        {/* Logo centered at top */}
        <div className="ptp-logo">
          <img src="/logo.png" alt="OC MENA Festival" />
        </div>

        {/* Gift label */}
        {ticket.gift_label && (
          <div className="ptp-gift-label">
            <span className="ptp-gift-icon">🎁</span>
            <span className="ptp-gift-text">{ticket.gift_label}</span>
          </div>
        )}

        {/* Ticket type */}
        <h1 className="ptp-ticket-type">{ticket.ticket_type_name}</h1>

        {/* AMP seat info */}
        {hasAmpInfo && (
          <div className="ptp-amp-info">
            {ampInfo.eventDate && (
              <div className="ptp-amp-row">
                <span className="ptp-amp-label">Date</span>
                <span className="ptp-amp-value">{formatDate(ampInfo.eventDate)}</span>
              </div>
            )}
            <div className="ptp-amp-row">
              <span className="ptp-amp-label">Section</span>
              <span className="ptp-amp-value">{ampInfo.section || '—'}</span>
            </div>
            <div className="ptp-amp-row">
              <span className="ptp-amp-label">Row</span>
              <span className="ptp-amp-value">{ampInfo.row || '—'}</span>
            </div>
            <div className="ptp-amp-row">
              <span className="ptp-amp-label">Seat</span>
              <span className="ptp-amp-value">{ampInfo.seat || '—'}</span>
            </div>
          </div>
        )}

        {/* Valid days for festival tickets */}
        {!hasAmpInfo && ticket.valid_days && Array.isArray(ticket.valid_days) && (
          <div className="ptp-valid-days">
            {ticket.valid_days.map((day, i) => (
              <div key={i} className="ptp-day-badge">{formatDate(day)}</div>
            ))}
          </div>
        )}

        {/* Animated QR code with red scan line */}
        {ticket.qr_code && (
          <div className="ptp-qr-section">
            {showLiveTimer && (
              <div className="ptp-live-timer">
                <div className="ptp-live-timer-top">
                  <span className="ptp-live-dot" />
                  <span className="ptp-live-time">{timeLabel}</span>
                  <span className="ptp-live-countdown">Refresh {String(countdown).padStart(2, '0')}s</span>
                </div>
                <div className="ptp-live-meta">
                  <span>{zoneLabel}</span>
                  {geoState.status === 'ready' && <span>Geo: {geoState.label}</span>}
                  {geoState.status !== 'ready' && geoState.label && <span>{geoState.label}</span>}
                </div>
              </div>
            )}
            <div className="ptp-qr-frame" ref={qrWrapRef}>
              <RotatingQrImg ticket={ticket} size={320} alt="Ticket QR Code" className="ptp-qr-image" />
              <div className="ptp-scan-line" ref={scanLineRef} />
            </div>
            <p className="ptp-qr-hint">Present this QR code at the gate</p>
          </div>
        )}

        {/* Ticket code */}
        <div className="ptp-ticket-code">
          <span className="ptp-code-label">Ticket Code</span>
          <span className="ptp-code-value">{ticket.ticket_code}</span>
        </div>

        {/* Wallet buttons */}
        <div className="ptp-wallet-buttons">
          <button className="ptp-wallet-badge-btn" onClick={handleAppleWallet}>
            <img src="/apple.webp" alt="Add to Apple Wallet" className="ptp-wallet-badge" />
          </button>
          <button
            className="ptp-wallet-badge-btn"
            onClick={handleGoogleWallet}
            disabled={walletLoading}
          >
            {walletLoading ? 'Creating…' : (
              <img
                src="/enUS_add_to_google_wallet_add-wallet-badge.svg"
                alt="Add to Google Wallet"
                className="ptp-wallet-badge"
              />
            )}
          </button>
        </div>
        {walletError && <p className="ptp-wallet-error">{walletError}</p>}

        {/* Optional links */}
        <div className="ptp-optional-links">
          <a href="/signup" className="ptp-opt-link">Create an account</a>
          <span className="ptp-opt-divider">·</span>
          <a href="https://ocmenafestival.com/app" className="ptp-opt-link">Download the app</a>
        </div>

        {/* Footer */}
        <div className="ptp-footer">
          <p>© 2026 MENA Events, Inc. All Rights Reserved.</p>
          <p className="ptp-venue">OC Fair & Event Center · 88 Fair Drive · Costa Mesa, CA 92626</p>
        </div>
      </div>
    </div>
  );
}

export default PublicTicketPage;
